/** @format */

// Visual storyboard for the branded PNG export layout. Renders the exact
// `buildExportLayout` output to the page using react-plotly.js — what you
// see here is byte-equivalent to what would be rasterized to PNG, minus
// the toImage scale=2 retina pass. Use this to iterate on title spacing,
// logo size, axis labels, etc. without going through the download cycle.

import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import type { Data, Layout } from "plotly.js";
import { useMemo } from "react";
import Plot from "react-plotly.js";

import {
  buildCitation,
  buildExportFilename,
  buildExportLayout,
  EXPORT_LAYOUT_DEFAULTS,
} from "@/utils/plotExport";

export const Route = createFileRoute("/dev/export-storyboard")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: ExportStoryboard,
});

// ---------------------------------------------------------------------------
// Sample data — synthesized to match the real shapes (lap-numbered scatter,
// vehicle-numbered violin, lap×driver heatmap) so we exercise the same
// layout branches the production widgets do.
// ---------------------------------------------------------------------------

interface SampleDriver {
  number: string;
  name: string;
  color: string;
}

const SAMPLE_DRIVERS: SampleDriver[] = [
  { number: "48", name: "A. Bowman", color: "#1c83c7" },
  { number: "9", name: "C. Elliott", color: "#03e2ff" },
  { number: "39", name: "Z. Smith", color: "#ffd166" },
  { number: "47", name: "R. Stenhouse Jr.", color: "#19a974" },
  { number: "1", name: "R. Chastain", color: "#ef476f" },
  { number: "2", name: "A. Cindric", color: "#7c3aed" },
  { number: "4", name: "N. Gragson", color: "#fbbf24" },
];

const LAPS = 98;

function makeLapTimeData(): Data[] {
  return SAMPLE_DRIVERS.map(driver => {
    const x: number[] = [];
    const y: number[] = [];
    for (let lap = 1; lap <= LAPS; lap++) {
      x.push(lap);
      // Pseudo-random jitter around 50.5s, deterministic per driver
      const seed = driver.number.charCodeAt(0) + lap * 0.07;
      const noise = Math.sin(seed) * 1.2 + Math.cos(seed * 2.3) * 0.8;
      y.push(50.5 + noise);
    }
    return {
      type: "scatter",
      mode: "lines",
      name: `${driver.number} ${driver.name}`,
      x,
      y,
      line: { color: driver.color, width: 1.5 },
    } as Data;
  });
}

function makeLapTimeLayout(): Partial<Layout> {
  return {
    margin: { l: 60, r: 5, b: 35, t: 5, pad: 10 },
    modebar: { orientation: "v" },
    xaxis: {
      tickprefix: "Lap ",
      gridcolor: "#444",
      tickfont: { color: "#fff", size: 10 },
      range: [1, LAPS],
      rangemode: "nonnegative",
    },
    yaxis: {
      tickformat: ".2f",
      gridcolor: "#444",
      tickfont: { color: "#fff", size: 10 },
    },
    paper_bgcolor: "rgba(0,0,0,255)",
    plot_bgcolor: "rgba(0,0,0,255)",
  };
}

function makeViolinData(): Data[] {
  return SAMPLE_DRIVERS.map(driver => {
    const ys: number[] = [];
    const xs: string[] = [];
    for (let lap = 1; lap <= LAPS; lap++) {
      const seed = driver.number.charCodeAt(0) + lap * 0.07;
      const noise = Math.sin(seed) * 1.2 + Math.cos(seed * 2.3) * 0.8;
      ys.push(50.5 + noise);
      xs.push(driver.number);
    }
    return {
      type: "violin",
      name: `${driver.number} ${driver.name}`,
      x: xs,
      y: ys,
      points: "all",
      pointpos: -0.5,
      side: "positive",
      box: { visible: true, fillcolor: driver.color, width: 1 },
      line: { color: driver.color, width: 2 },
      fillcolor: driver.color,
      fillopacity: 0.4,
      marker: { color: driver.color, size: 3 },
    } as Data;
  });
}

function makeViolinLayout(): Partial<Layout> {
  return {
    margin: { l: 60, r: 5, b: 35, t: 5, pad: 10 },
    xaxis: {
      tickprefix: "# ",
      tickfont: { color: "#fff", size: 10 },
    },
    yaxis: {
      tickformat: ".2f",
      gridcolor: "#444",
      tickfont: { color: "#fff", size: 10 },
    },
    paper_bgcolor: "rgba(0,0,0,255)",
    plot_bgcolor: "rgba(0,0,0,255)",
  };
}

function makeHeatmapData(): Data[] {
  const z: number[][] = SAMPLE_DRIVERS.map((driver, di) => {
    const row: number[] = [];
    for (let lap = 1; lap <= LAPS; lap++) {
      const seed = driver.number.charCodeAt(0) + lap * 0.07 + di * 0.13;
      const noise = Math.sin(seed) * 1.2 + Math.cos(seed * 2.3) * 0.8;
      row.push(50.5 + noise);
    }
    return row;
  });
  return [
    {
      type: "heatmap",
      x: Array.from({ length: LAPS }, (_, i) => i + 1),
      y: SAMPLE_DRIVERS.map((_, i) => i),
      z,
      colorscale: "Viridis",
      reversescale: true,
      colorbar: {
        tickfont: { color: "#fff", size: 10 },
        outlinewidth: 0,
        thickness: 12,
      },
    } as Data,
  ];
}

function makeHeatmapLayout(): Partial<Layout> {
  return {
    margin: { l: 60, r: 5, b: 35, t: 5, pad: 10 },
    xaxis: {
      type: "linear",
      tickprefix: "Lap ",
      tickfont: { color: "#fff", size: 10 },
      automargin: true,
    },
    yaxis: {
      autorange: "reversed",
      type: "linear",
      tickmode: "array",
      tickvals: SAMPLE_DRIVERS.map((_, i) => i),
      ticktext: SAMPLE_DRIVERS.map(d => `#${d.number}`),
      tickfont: { color: "#fff", size: 10 },
    },
    paper_bgcolor: "rgba(0,0,0,255)",
    plot_bgcolor: "rgba(0,0,0,255)",
  };
}

// ---------------------------------------------------------------------------
// Variants — each one feeds different inputs through buildExportLayout to
// exercise the various branches (subtitle composition, axis label override,
// heatmap legend opt-out).
// ---------------------------------------------------------------------------

interface Variant {
  id: string;
  description: string;
  build: () => {
    data: Data[];
    layout: Partial<Layout>;
    width: number;
    height: number;
  };
}

const SAMPLE_RUN = {
  raceName: "Jack Link's 500",
  track: "Talladega",
  startTime: "2026-04-29T13:00:00Z",
  lapNumber: 188,
  totalLaps: 188,
};

function makeVariant(
  id: string,
  description: string,
  args: {
    data: Data[];
    layout: Partial<Layout>;
    title: string;
    xAxisLabel?: string;
    yAxisLabel?: string;
    disableForceLegend?: boolean;
    /** Override run state to simulate mid-race vs finished. */
    lapNumber?: number;
    totalLaps?: number;
  }
): Variant {
  return {
    id,
    description,
    build: () =>
      buildExportLayout({
        data: args.data,
        layout: args.layout,
        liveWidth: 800,
        liveHeight: 600,
        filename: buildExportFilename({
          raceName: SAMPLE_RUN.raceName,
          widget: id,
          date: SAMPLE_RUN.startTime,
        }),
        citation: buildCitation(),
        title: args.title,
        raceName: SAMPLE_RUN.raceName,
        track: SAMPLE_RUN.track,
        lapNumber: args.lapNumber ?? SAMPLE_RUN.lapNumber,
        totalLaps: args.totalLaps ?? SAMPLE_RUN.totalLaps,
        xAxisLabel: args.xAxisLabel,
        yAxisLabel: args.yAxisLabel,
        disableForceLegend: args.disableForceLegend,
      }),
  };
}

function ExportStoryboard() {
  const variants: Variant[] = useMemo(
    () => [
      makeVariant(
        "lap-time",
        "Line chart with race finished — chart x-range drives the lap fragment in the subtitle.",
        {
          data: makeLapTimeData(),
          layout: makeLapTimeLayout(),
          title: "Lap Time",
          xAxisLabel: "Lap",
          yAxisLabel: "Lap Time (s)",
        }
      ),
      makeVariant(
        "lap-time-mid-race",
        "Same widget mid-race (lap 92 of 188) WITHOUT a chart range — falls back to 'Lap N of M' in the subtitle.",
        {
          data: makeLapTimeData(),
          // Strip the x-axis range to simulate mid-race default zoom
          layout: { ...makeLapTimeLayout(), xaxis: { tickprefix: "Lap " } },
          title: "Lap Time",
          xAxisLabel: "Lap",
          yAxisLabel: "Lap Time (s)",
          lapNumber: 92,
          totalLaps: 188,
        }
      ),
      makeVariant(
        "lap-time-violin",
        "Violin chart — vehicle-number x-axis, no lap fragment in subtitle.",
        {
          data: makeViolinData(),
          layout: makeViolinLayout(),
          title: "Lap Time Distribution",
          yAxisLabel: "Lap Time (s)",
        }
      ),
      makeVariant(
        "lap-time-heatmap",
        "Heatmap — `disableForceLegend: true` skips the legend strip entirely so the colorbar can breathe.",
        {
          data: makeHeatmapData(),
          layout: makeHeatmapLayout(),
          title: "Lap Time Heatmap",
          xAxisLabel: "Lap",
          yAxisLabel: "Driver",
          disableForceLegend: true,
        }
      ),
    ],
    []
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <header className="max-w-6xl mx-auto mb-6">
        <Link to="/dev" className="text-sm text-blue-400 hover:text-blue-300">
          ← Dev Preview
        </Link>
        <h1 className="text-2xl font-bold text-slate-100 mt-2">Export Storyboard</h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          Live preview of <code>buildExportLayout</code> output. Saves you the
          download/open cycle when iterating on title spacing, logo size, axis labels,
          etc. The visible plot is the <em>same</em> layout that gets rasterized to PNG
          (minus the scale=2 retina pass).
        </p>
        <details className="mt-3 text-xs text-slate-500">
          <summary className="cursor-pointer hover:text-slate-300">
            Current layout constants
          </summary>
          <pre className="mt-2 p-3 bg-slate-900 rounded border border-slate-800 overflow-x-auto">
            {JSON.stringify(EXPORT_LAYOUT_DEFAULTS, null, 2)}
          </pre>
        </details>
      </header>

      <div className="max-w-6xl mx-auto space-y-10">
        {variants.map(variant => (
          <VariantPanel key={variant.id} variant={variant} />
        ))}
      </div>
    </div>
  );
}

function VariantPanel({ variant }: { variant: Variant }) {
  const built = useMemo(() => variant.build(), [variant]);
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
      <header className="px-4 py-3 border-b border-slate-800">
        <h2 className="text-base font-semibold text-slate-100">{variant.id}</h2>
        <p className="text-xs text-slate-400 mt-1">{variant.description}</p>
        <p className="text-[11px] text-slate-500 mt-1">
          Canvas: {built.width} × {built.height}
        </p>
      </header>
      <div className="p-4 overflow-x-auto bg-black">
        {/* Wrap in a fixed-width container so the canvas isn't squished by
            the page width — horizontal scroll lets you see the full export. */}
        <div style={{ width: built.width, height: built.height }}>
          <Plot
            data={built.data}
            layout={built.layout}
            config={{
              staticPlot: true,
              displaylogo: false,
              responsive: false,
            }}
            style={{ width: built.width, height: built.height }}
            useResizeHandler={false}
          />
        </div>
      </div>
    </section>
  );
}
